"use client";

import type React from "react";
import { useAppTheme } from "../../../ThemeContext.tsx";

// Example Edge type for demonstration purposes
type Edge = {
  id: string;
  label?: string;
};

interface FlowEdgeProps {
  edge: Edge;
  sourcePosition: { x: number; y: number };
  targetPosition: { x: number; y: number };
  onEdgeClick: (edge: Edge) => void;
}

export const FlowEdge: React.FC<FlowEdgeProps> = ({
  edge,
  sourcePosition,
  targetPosition,
  onEdgeClick,
}) => {
  // Calculate control points for the curved path
  const dx = targetPosition.x - sourcePosition.x;
  const dy = targetPosition.y - sourcePosition.y;
  const curveIntensity = Math.min(Math.abs(dy) / 2, 100);

  const controlPoint1 = {
    x: sourcePosition.x,
    y: sourcePosition.y + curveIntensity,
  };
  const controlPoint2 = {
    x: targetPosition.x,
    y: targetPosition.y - curveIntensity,
  };

  const path = `
    M ${sourcePosition.x} ${sourcePosition.y}
    C ${controlPoint1.x} ${controlPoint1.y}
      ${controlPoint2.x} ${controlPoint2.y}
      ${targetPosition.x} ${targetPosition.y}
  `;

  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  return (
    // This <svg> is essential; the marker must live in an <svg> or parent <defs>.
    <svg
      style={{
        position: "absolute",
        pointerEvents: "none", // So clicks pass through unless we override
      }}
    >
      <defs>
        <marker
          id="arrowhead"
          // Large viewBox, but smaller markerWidth/Height => arrow is scaled down
          viewBox="0 0 10 10"
          markerWidth="5"
          markerHeight="5"
          refX="9" // Move arrow tip to the end of the path
          refY="5"
          orient="auto"
        >
          {/* Path is 10 wide & 10 high. If the marker is only 5 wide & 5 high, 
              it effectively shrinks the arrow by 50%. */}
          <path d="M0,0 L0,10 L10,5 Z" fill="rgb(75, 85, 99)" />
        </marker>
      </defs>

      <g style={{ pointerEvents: "auto" }} onClick={() => onEdgeClick(edge)}>
        {/* The main path (line) with reduced opacity */}
        <path
          d={path}
          stroke="rgb(75, 85, 99)"
          strokeWidth={1.5}
          fill="none"
          markerEnd="url(#arrowhead)"
          // Either "opacity" to affect the entire path or "strokeOpacity" for just the stroke
          style={{ cursor: "pointer", opacity: 0.5 }}
          orientation="auto"
        />
        <g
          transform={`translate(${
            (sourcePosition.x + targetPosition.x) / 2 - 30
          }, ${(sourcePosition.y + targetPosition.y) / 2 - 10})`}
        >
          <rect
            x="0"
            y="0"
            width="60"
            height="20"
            rx="4"
            fill="rgb(31, 41, 55)"
            style={{ cursor: "pointer" }}
          />
          <text
            x="30"
            y="14"
            textAnchor="middle"
            fill="white"
            fontSize="12"
            style={{ cursor: "pointer" }}
          >
            Edge
          </text>
        </g>
      </g>
    </svg>
  );
};
